import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Global singleton flag to prevent duplicate initialization across all entry points
// This works even if multiple files try to initialize Firebase
declare global {
  interface Window {
    __FIREBASE_INITIALIZED__?: boolean;
    __FIREBASE_APP__?: FirebaseApp;
    __FIREBASE_AUTH__?: Auth;
    __FIREBASE_DB__?: Firestore;
    __FIREBASE_STORAGE__?: FirebaseStorage;
  }
}

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

// Check if already initialized in window (for multiple entry points)
if (typeof window !== 'undefined' && window.__FIREBASE_INITIALIZED__) {
  console.log('[Firebase] Using existing Firebase initialization from window');
  app = window.__FIREBASE_APP__!;
  auth = window.__FIREBASE_AUTH__!;
  db = window.__FIREBASE_DB__!;
  storage = window.__FIREBASE_STORAGE__!;
} else {
  // Initialize Firebase app exactly once (guards against duplicate initialization)
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  
  // Initialize auth
  auth = getAuth(app);
  
  // Get Firestore database - use custom database name if provided, otherwise use (default)
  // Use default DB when unset, empty, or literally "default" (Firestore's default ID is "(default)")
  const rawDb = import.meta.env.VITE_FIREBASE_DATABASE_NAME;
  const databaseName =
    typeof rawDb === 'string' && rawDb.trim() !== '' && rawDb.toLowerCase() !== 'default'
      ? rawDb.trim()
      : undefined;
  db = databaseName ? getFirestore(app, databaseName) : getFirestore(app);
  storage = getStorage(app);

  // Store in window to prevent duplicate initialization
  if (typeof window !== 'undefined') {
    window.__FIREBASE_INITIALIZED__ = true;
    window.__FIREBASE_APP__ = app;
    window.__FIREBASE_AUTH__ = auth;
    window.__FIREBASE_DB__ = db;
    window.__FIREBASE_STORAGE__ = storage;
    console.log('[Firebase] Initialized Firebase - database:', databaseName ?? '(default)');
  }
}

export { auth, db, storage };

// Google Auth Provider
export const googleProvider = new GoogleAuthProvider();

// App ID for multi-tenant support
export const appId = import.meta.env.VITE_APP_ID || 'default-app-id';
